import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
// Api
import apolloStreamClient from 'api/apolloStreamClient';
import {
  PlayButtonClicked,
  PlayButtonClickedVariables,
} from 'api/agora/types/PlayButtonClicked';
import { PLAY_BUTTON_CLICKED } from 'api/agora/mutations';
// Types
import { GetStream_stream } from 'api/streams/types/GetStream';
// Helpers
import { StreamRecordDisplay } from 'helpers/streams';
import {
  getDeepLinkInitialTimeSec,
  parseMediaRecordId,
  parseMediaStartTimeSec,
} from 'helpers/mediaStartTime';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Ui
import VideoPlayer from 'ui/VideoPlayer/VideoPlayer';
// Styles
import styles from './StreamVideoPlayer.module.scss';

type StreamVideoPlayerProps = {
  stream: GetStream_stream;
  recordsList: StreamRecordDisplay[];
  refetchStream: () => void;
  currentVideoUrl?: string;
  onVideoChange?: (videoURL: string) => void;
};

const StreamVideoPlayer = ({
  stream,
  recordsList,
  refetchStream,
  currentVideoUrl,
  onVideoChange,
}: StreamVideoPlayerProps): JSX.Element | null => {
  const router = useRouter();
  const startTimeSec = parseMediaStartTimeSec(router.query);
  const startRecordId = parseMediaRecordId(router.query);
  const recordFromQuery =
    startRecordId == null
      ? undefined
      : recordsList.find((record) => record.id === startRecordId);
  const firstRecordUrl = recordsList?.[0]?.videoURL || '';
  const deepLinkVideoUrl = recordFromQuery?.videoURL || firstRecordUrl;
  const videoPlayerUrl = currentVideoUrl || deepLinkVideoUrl;
  const initialTimeSec = getDeepLinkInitialTimeSec({
    videoUrl: videoPlayerUrl,
    deepLinkVideoUrl,
    startTimeSec,
  });

  const [playButtonClicked] = useMutation<
    PlayButtonClicked,
    PlayButtonClickedVariables
  >(PLAY_BUTTON_CLICKED, {
    client: apolloStreamClient,
  });

  const handleOnPlayNextClick = () => {
    if (recordsList?.length) {
      const currentVideoIndex = recordsList.findIndex(
        (record) => record.videoURL === videoPlayerUrl
      );

      if (currentVideoIndex < recordsList.length - 1) {
        const nextVideoUrl = recordsList[currentVideoIndex + 1].videoURL;
        if (nextVideoUrl) {
          onVideoChange?.(nextVideoUrl);
        }
      }
    }
  };

  const handlePlayButtonClicked = (streamId: string) => {
    try {
      playButtonClicked({
        variables: {
          streamId,
        },
      });
    } catch (error) {
      showToast({ message: error?.message, type: 'error' });
    }
  };

  const handleVideoPlayerOnPlay = () => {
    const currentVideo = recordsList.find(
      (record) => record.videoURL === videoPlayerUrl
    );

    if (currentVideo) {
      handlePlayButtonClicked(currentVideo?.streamId);
    }
  };

  const handleVideoEnded = () => {
    if (recordsList?.length > 1) {
      const currentVideoIndex = recordsList.findIndex(
        (record) => record.videoURL === videoPlayerUrl
      );

      if (currentVideoIndex < recordsList.length - 1) {
        const nextVideoUrl = recordsList[currentVideoIndex + 1].videoURL;
        if (nextVideoUrl) {
          onVideoChange?.(nextVideoUrl);
        }
      }
    }
  };

  if (!recordsList?.length) {
    refetchStream();
    return null;
  }

  const currentVideoIndex = recordsList.findIndex(
    (record) => record.videoURL === videoPlayerUrl
  );
  const hasNextVideo =
    currentVideoIndex >= 0 && currentVideoIndex < recordsList.length - 1;
  const currentRecord =
    currentVideoIndex >= 0 ? recordsList[currentVideoIndex] : recordsList[0];
  const initialDurationSec =
    typeof currentRecord?.duration === 'number' && currentRecord.duration > 0
      ? currentRecord.duration
      : undefined;

  return (
    <div className={styles.root}>
      <div className={styles.videoContainer}>
        <VideoPlayer
          key={videoPlayerUrl}
          onPlay={handleVideoPlayerOnPlay}
          onEnded={handleVideoEnded}
          autoPlay={!!videoPlayerUrl}
          videoPoster={currentVideoUrl ? '' : stream.imageURL || ''}
          src={videoPlayerUrl}
          volumeControl="speaker"
          videoClassName={styles.videoPlayer}
          initialDurationSec={initialDurationSec}
          initialTimeSec={initialTimeSec}
          {...(hasNextVideo && {
            onPlayNext: handleOnPlayNextClick,
          })}
        />
      </div>
    </div>
  );
};

export default StreamVideoPlayer;
